import React, {useEffect, useRef, useState} from "react";
import socket, {emitNow, OFFLINE_TOAST} from "../utils/clientSocket";
import {useToasts} from "./ui/Toasts";
import LobbyCard from "./LobbyCard";
import Icon from "./ui/Icon";

// Lists open game offers. Your own offer (if any) is pinned to the top while you wait.
const Lobby = ({ownOffer, setOwnOffer}) => {
    const {push} = useToasts();
    const [globalLobby, setGlobalLobby] = useState([]);
    // The ref makes sure we always filter with the newest own offer id, even inside socket handlers
    const ownOfferId = useRef(ownOffer?.offerId || null)
    const acceptedOfferIds = useRef([])

    useEffect(() => {
        ownOfferId.current = ownOffer?.offerId || null;
        setGlobalLobby((prev) => prev.filter((o) => o.offerId !== ownOfferId.current));
    }, [ownOffer]);

    useEffect(() => {
        const handleLobby = ({roomUid, offers}) => {
            if (roomUid !== "global") return;
            setGlobalLobby(offers.filter((o) => o.offerId !== ownOfferId.current));
        };
        const handleOfferCreated = (offer) => {
            ownOfferId.current = offer.offerId;
            setOwnOffer(offer);
        };

        // Ask for the newest list whenever we (re)connect or come back to the tab, so the lobby is never stale
        const refresh = () => socket.emit("refreshLobby");
        const refreshWhenVisible = () => {
            if (document.visibilityState === "visible") refresh();
        };

        socket.on("updateLobby", handleLobby);
        socket.on("offerCreated", handleOfferCreated);
        socket.on("connect", refresh);
        document.addEventListener("visibilitychange", refreshWhenVisible);
        refresh();

        return () => {
            socket.off("updateLobby", handleLobby);
            socket.off("offerCreated", handleOfferCreated);
            socket.off("connect", refresh);
            document.removeEventListener("visibilitychange", refreshWhenVisible);
        };
    }, [setOwnOffer]);

    const handleAcceptGame = (offer) => {
        if (acceptedOfferIds.current.includes(offer.offerId)) return;
        if (!emitNow("acceptGameOffer", {offerId: offer.offerId})) {
            push(OFFLINE_TOAST);
            return;
        }
        acceptedOfferIds.current.push(offer.offerId)
        setGlobalLobby((prevState) => prevState.filter(o => o.offerId !== offer.offerId))
    };

    const cancelOffer = () => {
        socket.emit("cancelOffer");
        ownOfferId.current = null;
        setOwnOffer(null);
    }

    if (globalLobby.length === 0 && !ownOffer) {
        return (
            <div className="empty-state">
                <Icon name="sprout"/>
                <strong>No open games right now</strong>
                <span className="small">Pick a time control above and someone can join you.</span>
            </div>
        );
    }

    return (
        <div className="lobby-list">
            {ownOffer && <LobbyCard offer={ownOffer} isOwnOffer={true} onCancel={cancelOffer}/>}
            {globalLobby.map((offer) => (
                <LobbyCard key={offer.offerId} offer={offer} onClick={() => handleAcceptGame(offer)} isOwnOffer={false}/>
            ))}
        </div>
    );
};

export default Lobby;
